import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form, Modal, InputGroup, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaShoppingCart, FaPlus, FaMinus, FaInfoCircle, FaEdit, FaPercent, FaDollarSign, FaEye, FaUser, FaExclamationTriangle, FaSync } from 'react-icons/fa';
import supabase from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../../components/LoadingSpinner';

// ===== COMPONENTE DE ERRO =====
const CatalogErrorState = ({ error, onRetry }) => {
  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0">Catálogo de Produtos</h1>
      </div>
      
      <div className="modern-error-container">
        <div className="modern-error-content">
          <div className="error-icon">
            <FaExclamationTriangle />
          </div>
          <h3>Ops! Algo deu errado</h3>
          <p className="error-message">{error}</p>
          <div className="error-actions">
            <Button 
              variant="primary" 
              onClick={onRetry}
              className="me-3"
            >
              <FaSync className="me-2" />
              Tentar Novamente
            </Button>
            <Button 
              variant="outline-secondary"
              onClick={() => window.location.reload()}
            >
              Atualizar Página
            </Button>
          </div>
        </div>
      </div>
    </Container>
  );
};

// Componente do Modal de Configuração de Item
const ItemConfigModal = ({ show, onHide, item, onAddToCart, isAdmin, formatCurrency }) => {
  const [quantity, setQuantity] = useState(1);
  const [customPrice, setCustomPrice] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('value');
  const [showPreview, setShowPreview] = useState(false);

  // Resetar valores quando o modal abrir com um novo item
  useEffect(() => {
    if (item && show) {
      setQuantity(1);
      setCustomPrice(item.price);
      setDiscount(0);
      setDiscountType('value');
      setShowPreview(false);
    }
  }, [item, show]);

  // Calcular preço final com desconto
  const calculateFinalPrice = useCallback(() => {
    const basePrice = parseFloat(customPrice) || 0;
    const discountValue = parseFloat(discount) || 0;
    
    if (discountValue === 0) return basePrice;
    
    if (discountType === 'percentage') {
      return basePrice * (1 - discountValue / 100);
    } else {
      return Math.max(0, basePrice - discountValue);
    }
  }, [customPrice, discount, discountType]);

  // Calcular total do item
  const calculateItemTotal = useCallback(() => {
    return calculateFinalPrice() * quantity;
  }, [calculateFinalPrice, quantity]);

  // Calcular economia total
  const calculateSavings = useCallback(() => {
    const originalTotal = (item?.price || 0) * quantity;
    const finalTotal = calculateItemTotal();
    return originalTotal - finalTotal;
  }, [item, quantity, calculateItemTotal]);

  const handleAddToCart = () => {
    if (!item || quantity <= 0) return;

    const itemToAdd = {
      ...item,
      quantity: quantity,
      customPrice: parseFloat(customPrice),
      discount: parseFloat(discount) || 0,
      discountType: discountType,
      originalPrice: item.price
    };

    onAddToCart(itemToAdd);
    onHide();
  };

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
    }
  };

  if (!item) return null;

  const finalPrice = calculateFinalPrice();
  const itemTotal = calculateItemTotal();
  const savings = calculateSavings();
  const hasDiscount = discount > 0;
  const hasCustomPrice = parseFloat(customPrice) !== item.price;

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      centered
      size="lg"
      className="item-config-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FaShoppingCart className="me-2" />
          Configurar Item - {item.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          {/* Coluna da Esquerda - Informações do Produto */}
          <Col md={6}>
            <div className="product-info-section">
              <h6 className="section-title">Informações do Produto</h6>
              
              <div className="product-details">
                <h5 className="product-name">{item.name}</h5>
                <p className="product-description text-muted">{item.description}</p>
                
                <div className="price-info mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <span>Preço original:</span>
                    <span className="fw-bold">{formatCurrency(item.price)}</span>
                  </div>
                  
                  {item.estoque !== null && (
                    <div className="d-flex justify-content-between align-items-center">
                      <span>Estoque disponível:</span>
                      <span className={`fw-bold ${item.estoque <= 5 ? 'text-warning' : 'text-success'}`}>
                        {item.estoque} unidades
                      </span>
                    </div>
                  )}
                </div>

                {/* Quantidade */}
                <Form.Group className="mb-3">
                  <Form.Label>Quantidade *</Form.Label>
                  <div className="quantity-control d-flex align-items-center">
                    <Button 
                      variant="dark" 
                      size="sm"
                      onClick={() => handleQuantityChange(quantity - 1)}
                      disabled={quantity <= 1}
                    >
                      <FaMinus size={12} />
                    </Button>
                    <Form.Control
                      type="number"
                      min="1"
                      max={item.estoque || 999}
                      value={quantity}
                      onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                      className="text-center mx-2"
                      style={{ width: '80px' }}
                    />
                    <Button 
                      variant="dark" 
                      size="sm"
                      onClick={() => handleQuantityChange(quantity + 1)}
                      disabled={item.estoque && quantity >= item.estoque}
                    >
                      <FaPlus size={12} />
                    </Button>
                  </div>
                  {item.estoque && quantity >= item.estoque && (
                    <Form.Text className="text-warning">
                      Quantidade máxima disponível: {item.estoque}
                    </Form.Text>
                  )}
                </Form.Group>
              </div>
            </div>
          </Col>

          {/* Coluna da Direita - Configurações de Preço (Admin) */}
          <Col md={6}>
            {isAdmin && (
              <div className="admin-config-section">
                <h6 className="section-title">
                  <FaEdit className="me-2" />
                  Configurações de Administrador
                </h6>

                <Form.Group className="mb-3">
                  <Form.Label>Preço Personalizado</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>R$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={customPrice}
                      onChange={(e) => setCustomPrice(e.target.value)}
                      placeholder="0.00"
                    />
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Defina um preço personalizado para este item.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Desconto</Form.Label>
                  <Form.Select
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value)}
                  >
                    <option value="value">Valor fixo (R$)</option>
                    <option value="percentage">Porcentagem (%)</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Desconto</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      {discountType === 'percentage' ? <FaPercent /> : 'R$'}
                    </InputGroup.Text>
                    <Form.Control
                      type="number"
                      step={discountType === 'percentage' ? '1' : '0.01'}
                      min="0"
                      max={discountType === 'percentage' ? '100' : (parseFloat(customPrice) || 0)}
                      value={discount}
                      onChange={(e) => setDiscount(e.target.value)}
                      placeholder="0"
                    />
                  </InputGroup>
                  <Form.Text className="text-muted">
                    {discountType === 'percentage' 
                      ? 'Desconto em porcentagem (0-100%)' 
                      : 'Desconto em valor fixo (R$)'}
                  </Form.Text>
                </Form.Group>

                <div className="d-flex gap-2 mb-3">
                  <Button 
                    variant="outline-info" 
                    size="sm"
                    onClick={() => setShowPreview(!showPreview)}
                  >
                    <FaEye className="me-1" />
                    {showPreview ? 'Ocultar' : 'Mostrar'} Preview
                  </Button>
                </div>
              </div>
            )}

            {/* Preview do Item */}
            {(isAdmin && showPreview) || !isAdmin ? (
              <div className="item-preview-section">
                <h6 className="section-title">Preview do Item</h6>
                
                <Alert variant="info" className="mb-0">
                  <div className="preview-content">
                    <div className="d-flex justify-content-between mb-2">
                      <span>Preço unitário:</span>
                      <span>
                        {hasCustomPrice && isAdmin ? (
                          <>
                            <span className="text-decoration-line-through text-muted me-2">
                              {formatCurrency(item.price)}
                            </span>
                            <span className="fw-bold">{formatCurrency(parseFloat(customPrice))}</span>
                          </>
                        ) : (
                          <span className="fw-bold">{formatCurrency(item.price)}</span>
                        )}
                      </span>
                    </div>

                    {hasDiscount && isAdmin && (
                      <div className="d-flex justify-content-between mb-2 text-success">
                        <span>Desconto:</span>
                        <span>
                          -{discountType === 'percentage' 
                            ? `${discount}%`
                            : formatCurrency(parseFloat(discount) || 0)
                          }
                        </span>
                      </div>
                    )}

                    <div className="d-flex justify-content-between mb-2">
                      <span>Preço final unitário:</span>
                      <span className="fw-bold text-primary">
                        {formatCurrency(finalPrice)}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span>Quantidade:</span>
                      <span className="fw-bold">{quantity}</span>
                    </div>

                    <hr className="my-2" />

                    <div className="d-flex justify-content-between mb-2">
                      <span className="fw-bold">Total do item:</span>
                      <span className="fw-bold text-success fs-5">
                        {formatCurrency(itemTotal)}
                      </span>
                    </div>

                    {savings > 0 && isAdmin && (
                      <div className="d-flex justify-content-between text-success">
                        <span>Economia total:</span>
                        <span className="fw-bold">
                          {formatCurrency(savings)}
                        </span>
                      </div>
                    )}
                  </div>
                </Alert>
              </div>
            ) : null}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button 
          variant="dark" 
          onClick={handleAddToCart}
          disabled={quantity <= 0 || (item.estoque && quantity > item.estoque)}
        >
          <FaShoppingCart className="me-2" />
          Adicionar ao Carrinho
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ===== COMPONENTE PRINCIPAL =====
const CatalogPage = () => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('additional');
  const [cart, setCart] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [additionalItems, setAdditionalItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  // Estados do Modal de Configuração
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // ===== FUNÇÃO DE RETRY =====
  const handleRetry = useCallback(() => {
    setError(null);
    setLoading(true);
    setRetryCount(prev => prev + 1);
  }, []);

  // Função para carregar carrinho do banco de dados
  const loadCartFromDatabase = useCallback(async () => {
    if (!user?.id) {
      setCart([]);
      return;
    }

    try {
      console.log('Carregando carrinho do banco para usuário:', user.id);

      const { data, error } = await supabase
        .from('caex_carrinho_pedido')
        .select(`
          *,
          caex_produtos (
            id,
            nome,
            descricao,
            preco,
            categoria_id,
            estoque,
            visivel_no_catalogo,
            ativo
          )
        `)
        .eq('usuario_id', user.id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Transformar dados para o formato esperado pelos componentes
      const formattedCart = data.map(item => ({
        id: item.produto_id,
        name: item.caex_produtos.nome,
        description: item.caex_produtos.descricao,
        price: parseFloat(item.preco_original),
        originalPrice: parseFloat(item.preco_original),
        customPrice: item.preco_customizado ? parseFloat(item.preco_customizado) : parseFloat(item.preco_original),
        discount: parseFloat(item.desconto) || 0,
        discountType: item.tipo_desconto || 'value',
        quantity: item.quantidade,
        category: item.caex_produtos.categoria_id,
        estoque: item.caex_produtos.estoque,
        visivel_no_catalogo: item.caex_produtos.visivel_no_catalogo,
        // Dados extras (credencial, montador, etc.)
        ...(item.dados_extras || {}),
        // ID do item do carrinho para controle interno
        cartItemId: item.id
      }));

      setCart(formattedCart);
      console.log('Carrinho carregado do banco:', formattedCart);
    } catch (error) {
      console.error('Erro ao carregar carrinho do banco:', error);
      setCart([]);
    }
  }, [user]);

  // Função para adicionar item ao carrinho no banco de dados
  const addToCartDatabase = useCallback(async (item) => {
    if (!user?.id) {
      console.error('Usuário não autenticado');
      return false;
    }

    try {
      console.log('Adicionando item ao carrinho no banco:', item);

      const cartData = {
        usuario_id: user.id,
        produto_id: item.id,
        quantidade: item.quantity || 1,
        preco_original: parseFloat(item.originalPrice || item.price),
        preco_customizado: item.customPrice ? parseFloat(item.customPrice) : null,
        desconto: parseFloat(item.discount) || 0,
        tipo_desconto: item.discountType || 'value',
        dados_extras: {
          ...(item.credencialData && { credencialData: item.credencialData }),
          ...(item.montadorData && { montadorData: item.montadorData }),
          ...(item.category && { category: item.category })
        }
      };

      // Verificar se o item já existe no carrinho
      const { data: existingItem } = await supabase
        .from('caex_carrinho_pedido')
        .select('*')
        .eq('usuario_id', user.id)
        .eq('produto_id', item.id)
        .single();

      if (existingItem) {
        // Atualizar item existente
        const { error } = await supabase
          .from('caex_carrinho_pedido')
          .update({
            quantidade: existingItem.quantidade + (item.quantity || 1),
            preco_customizado: cartData.preco_customizado,
            desconto: cartData.desconto,
            tipo_desconto: cartData.tipo_desconto,
            dados_extras: cartData.dados_extras,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingItem.id);

        if (error) throw error;
        console.log('Item atualizado no carrinho');
      } else {
        // Adicionar novo item
        const { error } = await supabase
          .from('caex_carrinho_pedido')
          .insert([cartData]);

        if (error) throw error;
        console.log('Novo item adicionado ao carrinho');
      }

      // Recarregar carrinho
      await loadCartFromDatabase();
      return true;
    } catch (error) {
      console.error('Erro ao adicionar item ao carrinho no banco:', error);
      alert('Não foi possível adicionar o item ao carrinho. Tente novamente.');
      return false;
    }
  }, [user, loadCartFromDatabase]);

  // Função para atualizar quantidade no banco de dados
  const updateCartItemQuantityDatabase = useCallback(async (productId, quantity) => {
    if (!user?.id) return false;

    if (quantity <= 0) {
      return removeFromCartDatabase(productId);
    }

    try {
      const { error } = await supabase
        .from('caex_carrinho_pedido')
        .update({ 
          quantidade: quantity,
          updated_at: new Date().toISOString()
        })
        .eq('usuario_id', user.id)
        .eq('produto_id', productId);

      if (error) throw error;

      // Atualizar estado local
      setCart(prevCart =>
        prevCart.map(item =>
          item.id === productId ? { ...item, quantity } : item
        )
      );

      console.log('Quantidade atualizada no banco');
      return true;
    } catch (error) {
      console.error('Erro ao atualizar quantidade no banco:', error);
      alert('Não foi possível atualizar a quantidade. Tente novamente.');
      return false;
    }
  }, [user]);

  // Função para remover item do carrinho no banco de dados
  const removeFromCartDatabase = useCallback(async (productId) => {
    if (!user?.id) return false;

    try {
      // 1. Encontrar o item que será removido para obter seus dados
      const itemToRemove = cart.find(item => item.id === productId);

      if (!itemToRemove) {
        console.warn(`Produto com ID ${productId} não encontrado no carrinho local.`);
        // Mesmo assim, tenta remover do banco de dados, pois pode ser um problema de sincronia
      }

      // 2. Verificar se o produto está vinculado a uma credencial e excluí-la
      if (itemToRemove && itemToRemove.vinculado_credencial_expositor) {
        console.log(`Removendo credencial vinculada: ${itemToRemove.name}`);
        const { error: deleteError } = await supabase
          .from('caex_credenciais')
          .delete()
          .match({ 
              cpf: itemToRemove.cpf,
              evento_id: itemToRemove.evento_id 
            });

        if (deleteError) {
          console.error('Erro ao excluir a credencial da tabela caex_credenciais:', deleteError);
        } else {
          console.log('Credencial removida com sucesso da tabela caex_credenciais.');
        }
      }

      // 3. Verificar se o produto está vinculado a um montador e excluí-lo
      if (itemToRemove && itemToRemove.vinculado_montador) {
        console.log(`Removendo montador vinculado: ${itemToRemove.name}`);
        const { error: deleteError } = await supabase
          .from('caex_montadores')
          .delete()
          .match({ 
            cpf: itemToRemove.cpf,
            evento_id: itemToRemove.evento_id 
          });

        if (deleteError) {
          console.error('Erro ao excluir o montador da tabela caex_montadores:', deleteError);
        } else {
          console.log('Montador removido com sucesso da tabela caex_montadores.');
        }
      }

      // 4. Remover o item da tabela do carrinho
      const { error: cartError } = await supabase
        .from('caex_carrinho_pedido')
        .delete()
        .eq('usuario_id', user.id)
        .eq('produto_id', productId);

      if (cartError) throw cartError;

      // 5. Atualizar o estado local para refletir a mudança na UI
      setCart(prevCart => prevCart.filter(item => item.id !== productId));
      console.log(`Item com ID ${productId} removido do carrinho no banco.`);
      
      return true;

    } catch (error) {
      console.error('Erro ao remover item do carrinho no banco:', error);
      alert('Não foi possível remover o item do carrinho. Tente novamente.');
      return false;
    }
  }, [user, cart]);

  // Função para carregar categorias
  const fetchCategories = async () => {
    try {
      console.log('Carregando categorias...');
      
      const { data, error } = await supabase
        .from('caex_categorias')
        .select('*')
        .order('id', { ascending: true });
      
      if (error) {
        console.error('Erro ao buscar categorias:', error);
        throw error;
      }
      
      console.log('Categorias carregadas:', data);
      
      // Normalizar categorias
      const normalizedCategories = Array.isArray(data) ? data.map(cat => ({
        id: Number(cat.id),
        nome: String(cat.nome || `Categoria ${cat.id}`),
        descricao: cat.descricao || '',
        ativo: Boolean(cat.ativo)
      })) : [];
      
      setCategories(normalizedCategories);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      throw new Error('Não foi possível carregar as categorias de produtos.');
    }
  };

  // Função para carregar produtos
  const fetchProducts = async (isUserAdmin) => {
    try {
      console.log('Carregando produtos como', isUserAdmin ? 'ADMINISTRADOR' : 'EXPOSITOR');
      
      // Construir a query base
      let query = supabase
        .from('caex_produtos')
        .select('*')
        .eq('ativo', true);
      
      // Aplicar filtro baseado no perfil do usuário
      if (!isUserAdmin) {
        console.log('Aplicando filtro para mostrar apenas produtos visíveis para expositor');
        query = query.eq('visivel_no_catalogo', true);
      } else {
        console.log('Mostrando TODOS os produtos (modo administrador)');
      }
      
      // Executar a query
      const { data, error } = await query.order('id', { ascending: false });
      
      if (error) {
        console.error('Erro ao buscar produtos:', error);
        throw error;
      }
      
      console.log('Produtos carregados:', data);
      
      // Transformar os dados do Supabase para o formato esperado pelo componente
      const formattedProducts = data.map(product => ({
        id: product.id,
        name: product.nome,
        description: product.descricao || '',
        price: parseFloat(product.preco),
        category: product.categoria_id,
        estoque: product.estoque,
        visivel_no_catalogo: product.visivel_no_catalogo
      }));
      
      setAdditionalItems(formattedProducts);
      
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      throw new Error('Não foi possível carregar a lista de produtos.');
    }
  };

  // useEffect principal
  useEffect(() => {
    if (!user) {
      console.warn('Usuário não autenticado - redirecionando para login');
      navigate('/login');
      return;
    }

    console.log('Usuário autenticado:', user.id);
    
    // Função para carregar dados
    const loadData = async () => {
      try {
        // Verificar se o usuário tem permissão de administrador
        const adminCheck = hasPermission('all');
        setIsAdmin(adminCheck);
        
        console.log('Perfil do usuário:', adminCheck ? 'Administrador' : 'Expositor');
        
        // Carregar carrinho específico do usuário do banco de dados
        await loadCartFromDatabase();
        
        // Carregar categorias e produtos
        await fetchCategories();
        await fetchProducts(adminCheck);

        setLoading(false);
        
      } catch (error) {
        console.error('Erro no carregamento:', error);
        setError(error.message || 'Erro desconhecido no carregamento');
        setLoading(false);
      }
    };

    loadData();
  }, [user, hasPermission, navigate, loadCartFromDatabase, retryCount]);

  // Adicionar estilos personalizados
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .coming-soon-badge {
        background-color: #ffc107;
        color: #212529;
        font-size: 0.7rem;
        padding: 0.2rem 0.4rem;
        border-radius: 4px;
        font-weight: 500;
      }
      
      .modal-tab-disabled {
        color: #adb5bd !important;
        cursor: not-allowed !important;
        opacity: 0.6 !important;
        pointer-events: none;
      }
      
      .custom-tabs .nav-link:hover:not(.active):not(.modal-tab-disabled) {
        color: #495057;
        background-color: rgba(0, 0, 0, 0.02);
      }

      /* ===== ERROR STATE STYLES ===== */
      .modern-error-container {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 60vh;
      }

      .modern-error-content {
        text-align: center;
        padding: 3rem 2rem;
        background: white;
        border-radius: 24px;
        box-shadow: 0 25px 80px rgba(0, 0, 0, 0.1);
        border: 1px solid rgba(220, 53, 69, 0.1);
        max-width: 500px;
        width: 100%;
      }

      .error-icon {
        color: #dc3545;
        margin-bottom: 1.5rem;
        animation: pulse 2s infinite;
        font-size: 3rem;
      }

      @keyframes pulse {
        0%, 100% { 
          transform: scale(1); 
          opacity: 0.8;
        }
        50% { 
          transform: scale(1.05); 
          opacity: 1;
        }
      }

      .modern-error-content h3 {
        color: #1a1a1a;
        margin-bottom: 1rem;
        font-weight: 600;
        font-size: 1.5rem;
      }

      .error-message {
        color: #6c757d;
        margin-bottom: 2rem;
        font-size: 1.1rem;
        line-height: 1.5;
      }

      .error-actions {
        display: flex;
        justify-content: center;
        gap: 1rem;
        flex-wrap: wrap;
      }

      .admin-mode-badge {
        background-color: #17a2b8;
        color: white;
        font-size: 0.8rem;
        padding: 0.35rem 0.7rem;
        border-radius: 20px;
        font-weight: 600;
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        margin-left: 1rem;
      }
      
      .admin-only-badge {
        background-color: rgba(23, 162, 184, 0.15);
        color: #17a2b8;
        font-size: 0.7rem;
        font-weight: 600;
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
        margin-left: 0.5rem;
      }

      .user-info-badge {
        background-color: #28a745;
        color: white;
        font-size: 0.8rem;
        padding: 0.35rem 0.7rem;
        border-radius: 20px;
        font-weight: 600;
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        margin-left: 1rem;
      }

      /* Estilos do Modal de Configuração */
      .item-config-modal .modal-dialog {
        max-width: 800px;
      }

      .section-title {
        color: #495057;
        font-weight: 600;
        margin-bottom: 1rem;
        padding-bottom: 0.5rem;
        border-bottom: 2px solid #e9ecef;
      }

      .product-info-section {
        background-color: #f8f9fa;
        padding: 1.5rem;
        border-radius: 8px;
        height: 100%;
      }

      .admin-config-section {
        background-color: #fff3cd;
        padding: 1.5rem;
        border-radius: 8px;
        border: 1px solid #ffeaa7;
        margin-bottom: 1rem;
      }

      .item-preview-section {
        background-color: #d1ecf1;
        padding: 1.5rem;
        border-radius: 8px;
        border: 1px solid #bee5eb;
      }

      .quantity-control {
        max-width: 200px;
      }

      .preview-content {
        font-size: 0.95rem;
      }

      .product-name {
        color: #2c3e50;
        margin-bottom: 0.5rem;
      }

      .product-description {
        font-size: 0.9rem;
        line-height: 1.4;
        margin-bottom: 1rem;
      }

      .price-info {
        background-color: white;
        padding: 1rem;
        border-radius: 6px;
        border: 1px solid #dee2e6;
      }

      /* ===== RESPONSIVE ===== */
      @media (max-width: 768px) {
        .modern-error-content {
          padding: 2rem 1.5rem;
          margin: 1rem;
        }
        
        .error-actions {
          flex-direction: column;
        }
        
        .error-actions .btn {
          width: 100%;
          margin-bottom: 0.5rem;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Dados simulados para os espaços (mantidos como estavam)
  const spaces = [
    {
      id: 1,
      name: "Estande Básico 9m²",
      description: "Espaço básico de 9m² (3x3m) em localização padrão. Inclui montagem básica, iluminação e uma tomada.",
      price: 5000
    },
    {
      id: 2,
      name: "Estande Premium 16m²",
      description: "Espaço premium de 16m² (4x4m) em localização privilegiada. Inclui montagem personalizada e 2 tomadas.",
      price: 9200
    },
    {
      id: 3,
      name: "Estande VIP 25m²",
      description: "Espaço VIP de 25m² (5x5m) em localização central. Inclui montagem premium e 3 tomadas.",
      price: 14500
    }
  ];
  
  // Filtrar itens adicionais por categoria
  const filteredAdditionalItems = categoryFilter 
    ? additionalItems.filter(item => item.category === Number(categoryFilter))
    : additionalItems;

  // Obter categorias únicas dos produtos carregados
  const uniqueCategories = Array.from(new Set(additionalItems.map(item => item.category)));
  
  // Formatação de valores monetários
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };
  
  // Abrir modal de configuração
  const openConfigModal = (item) => {
    setSelectedItem(item);
    setShowConfigModal(true);
  };

  // Adicionar item ao carrinho (versão melhorada)
  const addToCartFromModal = async (configuredItem) => {
    const success = await addToCartDatabase(configuredItem);
    if (success) {
      console.log('Item adicionado ao carrinho com sucesso');
    }
  };

  // Adicionar item ao carrinho (versão simples - mantida para compatibilidade)
  const addToCart = (item) => {
    openConfigModal(item);
  };
  
  // Remover item do carrinho
  const removeFromCart = async (itemId) => {
    await removeFromCartDatabase(itemId);
  };
  
  // Atualizar quantidade de item no carrinho
  const updateCartItemQuantity = async (itemId, quantity) => {
    await updateCartItemQuantityDatabase(itemId, quantity);
  };
  
  // Calcular total do carrinho
  const calculateCartTotal = () => {
    return cart.reduce((total, item) => {
      const finalPrice = item.customPrice || item.price;
      const discount = item.discount || 0;
      
      let itemPrice = finalPrice;
      if (discount > 0) {
        if (item.discountType === 'percentage') {
          itemPrice = finalPrice * (1 - discount / 100);
        } else {
          itemPrice = Math.max(0, finalPrice - discount);
        }
      }
      
      return total + (itemPrice * item.quantity);
    }, 0);
  };

  // Calcular quantidade total de itens
  const getTotalCartItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };
  
  // Ir para finalização do pedido
  const goToCheckout = () => {
    navigate('/catalog/new-order');
  };

  // Verificar se item está no carrinho
  const getItemQuantityInCart = (itemId) => {
    const cartItem = cart.find(item => item.id === itemId);
    return cartItem ? cartItem.quantity : 0;
  };

  // Obter o nome da categoria a partir do ID
  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.nome : 'Categoria não encontrada';
  };

  // ===== RENDERIZAÇÃO =====

  // Verificar se usuário está logado
  if (!user) {
    return (
      <Container fluid>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Card className="text-center p-4">
            <Card.Body>
              <FaUser size={48} className="text-muted mb-3" />
              <h4>Acesso Restrito</h4>
              <p className="text-muted">Você precisa estar logado para acessar o catálogo.</p>
              <Button 
                variant="primary" 
                onClick={() => navigate('/login')}
                className="mt-3"
              >
                Fazer Login
              </Button>
            </Card.Body>
          </Card>
        </div>
      </Container>
    );
  }
  
  // Estado de carregamento
  if (loading) {
    return (
      <LoadingSpinner 
        message="Carregando catálogo de produtos..."
        size="lg"
        overlay={true}
      />
    );
  }

  // Estado de erro
  if (error) {
    return <CatalogErrorState error={error} onRetry={handleRetry} />;
  }
  
  return (
    <Container fluid>
      {/* Mensagem de sucesso no carregamento */}
      <Alert variant="success" className="d-flex align-items-center mb-4">
        <FaInfoCircle className="me-2" />
        Catálogo carregado com sucesso! {additionalItems.length} produtos disponíveis.
      </Alert>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="mb-0">
            Catálogo de Produtos
            {/* Indicador de modo administrador */}
            {isAdmin && (
              <span className="admin-mode-badge">
                <FaInfoCircle size={14} />
                Modo Administrador
              </span>
            )}
            {/* Indicador de usuário logado */}
            <span className="user-info-badge">
              <FaUser size={14} />
              {user.email}
            </span>
          </h1>
          {isAdmin && (
            <small className="text-muted d-block mt-1">
              Você está vendo todos os produtos disponíveis, incluindo os ocultos para expositores.
            </small>
          )}
        </div>
        
        <Button 
          variant="dark" 
          onClick={goToCheckout}
          className="d-flex align-items-center"
          size="lg"
        >
          <FaShoppingCart className="me-2" />
          Ver Carrinho 
          {cart.length > 0 && (
            <Badge bg="primary" className="ms-2">
              {getTotalCartItems()}
            </Badge>
          )}
        </Button>
      </div>
      
      {/* Substituir o componente Tabs por uma versão personalizada */}
      <div className="custom-tabs mb-4">
        <ul className="nav nav-tabs">
          <li className="nav-item">
            <a 
              className="nav-link modal-tab-disabled"
              href="#" 
              onClick={(e) => e.preventDefault()}
              title="Funcionalidade em desenvolvimento"
            >
              Espaços em Pavilhões
              <span className="coming-soon-badge ms-2">Em breve</span>
            </a>
          </li>
          <li className="nav-item">
            <a 
              className="nav-link active"
              href="#" 
              onClick={(e) => e.preventDefault()}
            >
              Itens Adicionais
            </a>
          </li>
        </ul>
      </div>
      
      {/* Conteúdo da aba de itens adicionais */}
      <div className="mb-4">
        <Row>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Filtrar por categoria</Form.Label>
              <Form.Select 
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">Todas as categorias</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.nome}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={8}>
            <div className="d-flex flex-wrap gap-2 mt-4">
              <Button 
                variant={categoryFilter === '' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setCategoryFilter('')}
              >
                Todas
              </Button>
              {categories.map(category => (
                <Button 
                  key={category.id}
                  variant={categoryFilter === String(category.id) ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setCategoryFilter(String(category.id))}
                >
                  {category.nome}
                </Button>
              ))}
            </div>
          </Col>
        </Row>
      </div>
      
      {/* Mensagem se não houver produtos */}
      {filteredAdditionalItems.length === 0 && (
        <div className="text-center p-5 bg-light rounded mb-4">
          <h4>Nenhum produto encontrado</h4>
          <p>Não há produtos disponíveis nesta categoria.</p>
        </div>
      )}
      
      {/* Lista de produtos */}
      <Row>
        {filteredAdditionalItems.map(item => {
          const quantityInCart = getItemQuantityInCart(item.id);
          const categoryName = getCategoryName(item.category);
          const isAdminOnly = item.visivel_no_catalogo === false;
          
          return (
            <Col lg={3} md={6} className="mb-4" key={item.id}>
              <Card className={`h-100 shadow-sm card-product ${quantityInCart > 0 ? 'border-primary' : ''}`}>
                <Card.Body className="d-flex flex-column">
                  <div className="mb-2">
                    <Badge bg="secondary">{categoryName}</Badge>
                    {quantityInCart > 0 && (
                      <Badge bg="primary" className="ms-1">
                        {quantityInCart} no carrinho
                      </Badge>
                    )}
                    {/* Badge de administrador para produtos não visíveis para expositores */}
                    {isAdmin && isAdminOnly && (
                      <Badge className="admin-only-badge">
                        Apenas Admin
                      </Badge>
                    )}
                  </div>
                  <Card.Title>{item.name}</Card.Title>
                  <Card.Text className="text-muted small">{item.description}</Card.Text>
                  <div className="mt-auto">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="text-primary mb-0">{formatCurrency(item.price)}</h5>
                      <Button 
                        variant="dark" 
                        size="sm"
                        onClick={() => addToCart(item)}
                        disabled={item.estoque !== null && item.estoque <= 0}
                      >
                        <FaShoppingCart className="me-1" />
                        {item.estoque !== null && item.estoque <= 0 ? 'Esgotado' : 'Adicionar'}
                      </Button>
                    </div>
                    {item.estoque !== null && (
                      <small className="text-muted d-block mt-1">
                        {item.estoque > 0 
                          ? `Disponível: ${item.estoque} unidades` 
                          : 'Produto fora de estoque'}
                      </small>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Modal de Configuração de Item */}
      <ItemConfigModal 
        show={showConfigModal}
        onHide={() => setShowConfigModal(false)}
        item={selectedItem}
        onAddToCart={addToCartFromModal}
        isAdmin={isAdmin}
        formatCurrency={formatCurrency}
      />
    </Container>
  );
};

export default CatalogPage;